import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ApiUrl from "../Environment/ApiUrl";
import { useToast } from "../Toast/ToastContext";
import { RatesContext } from "../Pages/RatesContext";
import { Modal, Button } from "react-bootstrap"; // Import Bootstrap Modal components

function AddDailyRates() {
  const navigate = useNavigate();
  const toast = useToast();
  const { updateRates } = useContext(RatesContext);

  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const [addData, setAddData] = useState({
    gold_1g_rate: "",
    cold_coin_rate: "",
    gold_pure_percentage: "",
    silver_10g_rate: "",
    silver_coin_rate: "",
    silver_pure_percentage: "",
    date: getCurrentDate(),
    time: getCurrentTime(),
    created_by: "",
    created_date: "",
    updated_by: "",
    updated_date: "",
  });

  // State for controlling the modal
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null); // To track which action to perform on confirm
  const [existingRates, setExistingRates] = useState(null); // To store existing rates data

  // Validation to check if all required fields are filled
  const isFormValid = () => {
    return (
      addData.gold_1g_rate.trim() !== "" &&
      addData.cold_coin_rate.trim() !== "" &&
      addData.gold_pure_percentage.trim() !== "" &&
      addData.silver_10g_rate.trim() !== "" &&
      addData.silver_coin_rate.trim() !== "" &&
      addData.silver_pure_percentage.trim() !== "" &&
      addData.date.trim() !== "" &&
      addData.time.trim() !== ""
    );
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const parts = dateString.split("-");
    if (parts.length === 3) {
      return parts.reverse().join("-");
    }
    return dateString;
  };

  // Check if rates exist for the current date
  useEffect(() => {
    const checkExistingRates = async () => {
      try {
        const response = await axios.get(
          `${ApiUrl}/Api/daily_rates.php/current`
        );
        if (
          response.data.status &&
          response.data.data &&
          response.data.data.date === formatDateForDisplay(getCurrentDate())
        ) {
          setExistingRates(response.data.data);
          setModalAction("preload");
          setShowModal(true); // Show modal instead of window.confirm
        }
      } catch (error) {
        console.error("Error checking existing rates:", error.message);
      }
    };
    checkExistingRates();
  }, []);

  // Handle modal confirmation
  const handleModalConfirm = async () => {
    if (modalAction === "preload") {
      setAddData({
        ...addData,
        gold_1g_rate: existingRates.gold_1g_rate,
        cold_coin_rate: existingRates.cold_coin_rate,
        gold_pure_percentage: existingRates.gold_pure_percentage,
        silver_10g_rate: existingRates.silver_10g_rate,
        silver_coin_rate: existingRates.silver_coin_rate,
        silver_pure_percentage: existingRates.silver_pure_percentage,
        date: getCurrentDate(),
        time: getCurrentTime(),
      });
    } else if (modalAction === "submit") {
      await submitUpdate();
    }
    setShowModal(false);
  };

  // Handle form submission
  const handlesubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      toast.error("Please fill all required fields", 3000, "topRight");
      return;
    }

    try {
      const inputDate = addData.date;
      const formattedDate = inputDate
        ? inputDate.split("-").reverse().join("-")
        : "";
      const formattedTime = addData.time.trim();

      const formData = {
        gold_1g_rate: addData.gold_1g_rate,
        cold_coin_rate: addData.cold_coin_rate,
        gold_pure_percentage: addData.gold_pure_percentage,
        silver_10g_rate: addData.silver_10g_rate,
        silver_coin_rate: addData.silver_coin_rate,
        silver_pure_percentage: addData.silver_pure_percentage,
        date: formattedDate,
        time: formattedTime,
        created_by: addData.created_by || "admin",
        created_date: addData.created_date || new Date().toISOString(),
        updated_by: addData.updated_by || "admin",
        updated_date: addData.updated_date || new Date().toISOString(),
      };

      // Check if rates exist for the current date
      const checkResponse = await axios.get(
        `${ApiUrl}/Api/daily_rates.php/current`
      );
      if (
        checkResponse.data.status &&
        checkResponse.data.data &&
        checkResponse.data.data.date === formattedDate
      ) {
        setExistingRates(checkResponse.data.data);
        setModalAction("submit");
        setShowModal(true); // Show modal instead of window.confirm
      } else {
        // Send POST request to add a new record
        const response = await axios.post(
          `${ApiUrl}/Api/daily_rates.php`,
          formData
        );

        if (response.data.code === 201) {
          updateRates({
            gold_1g_rate: addData.gold_1g_rate,
            silver_10g_rate: addData.silver_10g_rate,
          });

          navigate("/daily-rates");
          toast.success("Added Successfully!", 3000, "topRight");

          setAddData({
            gold_1g_rate: "",
            cold_coin_rate: "",
            gold_pure_percentage: "",
            silver_10g_rate: "",
            silver_coin_rate: "",
            silver_pure_percentage: "",
            date: getCurrentDate(),
            time: getCurrentTime(),
            created_by: "",
            created_date: "",
            updated_by: "",
            updated_date: "",
          });
        } else {
          toast.error(
            response.data.message || "Failed to Add Data",
            3000,
            "topRight"
          );
        }
      }
    } catch (error) {
      console.error("Error processing data:", error.message);
      toast.error("Failed to Process Data", 3000, "topRight");
    }
  };

  // Function to handle update submission
  const submitUpdate = async () => {
    try {
      const inputDate = addData.date;
      const formattedDate = inputDate
        ? inputDate.split("-").reverse().join("-")
        : "";
      const formattedTime = addData.time.trim();

      const formData = {
        gold_1g_rate: addData.gold_1g_rate,
        cold_coin_rate: addData.cold_coin_rate,
        gold_pure_percentage: addData.gold_pure_percentage,
        silver_10g_rate: addData.silver_10g_rate,
        silver_coin_rate: addData.silver_coin_rate,
        silver_pure_percentage: addData.silver_pure_percentage,
        date: formattedDate,
        time: formattedTime,
        created_by: addData.created_by || "admin",
        created_date: addData.created_date || new Date().toISOString(),
        updated_by: addData.updated_by || "admin",
        updated_date: addData.updated_date || new Date().toISOString(),
      };

      const updateResponse = await axios.put(
        `${ApiUrl}/Api/daily_rates.php/${existingRates.id}`,
        formData
      );

      if (updateResponse.data.code === 200) {
        updateRates({
          gold_1g_rate: addData.gold_1g_rate,
          silver_10g_rate: addData.silver_10g_rate,
        });

        navigate("/daily-rates");
        toast.success("Updated Successfully!", 3000, "topRight");

        setAddData({
          gold_1g_rate: "",
          cold_coin_rate: "",
          gold_pure_percentage: "",
          silver_10g_rate: "",
          silver_coin_rate: "",
          silver_pure_percentage: "",
          date: getCurrentDate(),
          time: getCurrentTime(),
          created_by: "",
          created_date: "",
          updated_by: "",
          updated_date: "",
        });
      } else {
        toast.error(
          updateResponse.data.message || "Failed to Update Data",
          3000,
          "topRight"
        );
      }
    } catch (error) {
      console.error("Error updating data:", error.message);
      toast.error("Failed to Update Data", 3000, "topRight");
    }
  };

  return (
    <div className="container-fluid mb-5">
      <div className="card p-2">
        <div className="d-flex justify-content-between align-items-center">
          <h6>Add Today's Rates</h6>
          <div className="d-flex align-items-center ms-auto">
            <div className="d-flex align-items-center me-3">
              <label htmlFor="date" className="me-2">
                Date:
              </label>
              <input
                type="date"
                className="form-control m-2"
                value={addData.date}
                onChange={(e) =>
                  setAddData({ ...addData, date: e.target.value })
                }
                required
              />
            </div>
            <div className="d-flex align-items-center">
              <label htmlFor="time" className="me-2">
                Time:
              </label>
              <input
                type="time"
                className="form-control m-2"
                value={addData.time}
                onChange={(e) =>
                  setAddData({ ...addData, time: e.target.value })
                }
                required
              />
            </div>
          </div>
        </div>
        <div className="card mt-2 bg-warning bg-opacity-10">
          <div className="card-body">
            <div className="row text-center">
              <div className="col-md-4">
                <h6>Current Gold Rate</h6>
                <p>
                  <span className="mx-1">₹</span>
                  {addData.gold_1g_rate} / 1 gram
                </p>
              </div>
              <div className="col-md-4">
                <h6>Current Silver Rate</h6>
                <p>
                  <span className="mx-1">₹</span>
                  {addData.silver_10g_rate} / 1 gram
                </p>
              </div>
              <div className="col-md-4">
                <h6>Current Date</h6>
                <p>{formatDateForDisplay(addData.date)}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-md-6">
            <div className="card">
              <div className="card-header bg-white">
                <h6 className="mb-0 text-primary text-center">Gold Rates</h6>
              </div>
              <div className="card-body">
                <div>
                  <label className="form-label">Gold 1g-Rate</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]+(\.[0-9]*)?"
                    className="form-control rounded-pill"
                    value={addData.gold_1g_rate}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        setAddData({ ...addData, gold_1g_rate: value });
                      }
                    }}
                    placeholder="Enter Gold 1g-Rate"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Gold Coin-Rate</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]+(\.[0-9]*)?"
                    className="form-control rounded-pill"
                    value={addData.cold_coin_rate}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        setAddData({ ...addData, cold_coin_rate: value });
                      }
                    }}
                    placeholder="Enter Gold Coin-Rate"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Gold Pure Rate</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]+(\.[0-9]*)?"
                    className="form-control rounded-pill"
                    value={addData.gold_pure_percentage}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        setAddData({
                          ...addData,
                          gold_pure_percentage: value,
                        });
                      }
                    }}
                    placeholder="Enter Gold Pure Rate"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card">
              <div className="card-header bg-white">
                <h6 className="mb-0 text-primary text-center">Silver Rates</h6>
              </div>
              <div className="card-body">
                <div>
                  <label className="form-label">Silver 1g-Rate</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]+(\.[0-9]*)?"
                    className="form-control rounded-pill"
                    id="silver10g"
                    name="silver10g"
                    value={addData.silver_10g_rate}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        setAddData({
                          ...addData,
                          silver_10g_rate: value,
                        });
                      }
                    }}
                    placeholder="Enter Silver 1g-Rate"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Silver Coin-Rate</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]+(\.[0-9]*)?"
                    className="form-control rounded-pill"
                    id="silverCoin"
                    name="silverCoin"
                    value={addData.silver_coin_rate}
                    onChange={(e) => {
                      const value = e.target.value;
                     if (/^\d*\.?\d*$/.test(value)) {
                        setAddData({
                          ...addData,
                          silver_coin_rate: value,
                        });
                      }
                    }}
                    placeholder="Enter Silver Coin-Rate"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Silver Pure Rate</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]+(\.[0-9]*)?"
                    className="form-control rounded-pill"
                    id="silverPurePercentage"
                    name="silverPurePercentage"
                    value={addData.silver_pure_percentage}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        setAddData({
                          ...addData,
                          silver_pure_percentage: value,
                        });
                      }
                    }}
                    placeholder="Enter Silver Pure Rate"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-center mt-2">
            <button
              className="btn btn-secondary"
              style={{ fontSize: "14px", fontWeight: "600" }}
              onClick={() => navigate("/daily-rates")}
            >
              Back
            </button>
            <button
              className="btn btn-primary mx-2"
              style={{ fontSize: "14px", fontWeight: "600" }}
              onClick={handlesubmit}
              disabled={!isFormValid()}
            >
              Submit
            </button>
          </div>
        </div>
      </div>

      {/* Bootstrap Modal for confirming update */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Rates</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Rates for {formatDateForDisplay(getCurrentDate())} already exist.
          Would you like to update them?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => navigate("/daily-rates")}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleModalConfirm}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AddDailyRates;
